import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Wallet, ClockAlert, CalendarCheck, ChevronRight, TrendingDown } from "lucide-react";
import api from "@/lib/api";
import { formatIDR, fmtDate } from "@/lib/constants";
import { useAccess } from "@/lib/access";

/**
 * Widget Dashboard: Top 5 overdue invoices + Total janji bayar minggu ini.
 * BOD-friendly quick view; klik "Buka Collection Center" untuk detail.
 */
export default function CollectionDashboardWidget({ user }) {
  const nav = useNavigate();
  const { canPerm } = useAccess();
  const isEligible = !!user && (canPerm(user, "collection", "view") || canPerm(user, "projects", "view"));
  const [summary, setSummary] = useState(null);
  const [top5, setTop5] = useState([]);
  const [overdueDetail, setOverdueDetail] = useState(null);  // 2026-10-17: single source kartu+Top5
  const [overSummary, setOverSummary] = useState(null);
  const [arReport, setArReport] = useState(null);  // 2026-10-13 (R166 Fase 2): engine terpadu
  const [draftStats, setDraftStats] = useState(null);  // statistik kanonis invoice draft
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isEligible) return;
    setLoading(true);
    Promise.all([
      api.get("/collection/summary").then(({ data }) => data).catch(() => null),
      // 2026-10-17 (user request): kartu Piutang Jatuh Tempo DAN Top 5 Terlambat
      // dari SATU endpoint engine Rekonsiliasi A.R. — tidak lagi dua sumber.
      api.get("/ar/overdue-detail").then(({ data }) => data).catch(() => null),
      // 2026-10-22 (user request "SINKRONISASI ANTREAN KLASIFIKASI"):
      // badge "perlu klasifikasi" dibaca dari agregasi antrean kanonis
      // (/ar/receipts/queue) — bukan /overreceived/flagged — agar Dashboard
      // counter = total antrean yang dapat dibuka FAT.
      api.get("/ar/receipts/queue").then(({ data }) => ({
        unclassified: data?.needs_classification_projects || 0,
        total_excess: data?.needs_classification_amount || 0,
        total_items: data?.needs_classification_count || 0,
      })).catch(() => null),
      // R166: retensi dari engine yang sama (ar_engine).
      api.get("/ar/outstanding-report").then(({ data }) => data).catch(() => null),
      api.get("/collection/draft-queue/stats").then(({ data }) => data).catch(() => null),
    ]).then(([sum, od, over, ar, ds]) => {
      setSummary(sum);
      setDraftStats(ds);
      setOverdueDetail(od);
      setTop5(od?.top5 || []);
      setOverSummary(over);
      setArReport(ar);
    }).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  if (!isEligible) return null;

  // Filter janji bayar 7 hari ke depan (sudah dari endpoint summary).
  const promiseThisWeek = (summary?.upcoming_promises || []);
  const promiseTotal = promiseThisWeek.reduce((s, p) => s + (Number(p.promise_amount) || 0), 0);
  // 2026-10-17: kartu & jumlah invoice SELALU dari overdue-detail (engine A.R.);
  // fallback engine lama hanya bila endpoint gagal termuat.
  const arMetrics = arReport?.metrics;
  const overdueVal = overdueDetail ? overdueDetail.overdue_total : (arMetrics ? arMetrics.ar_overdue : (summary?.total_overdue || 0));
  const overdueCnt = overdueDetail ? overdueDetail.overdue_count : (arMetrics ? (arReport.invoice_rows || []).filter((r) => r.is_overdue).length : (summary?.overdue_count || 0));
  const retentionVal = arMetrics ? arMetrics.retention_not_claimed_total : (summary?.retention_active || 0);
  const asOf = overdueDetail?.as_of || arReport?.as_of;
  const noDue = overdueDetail?.no_due_date;

  return (
    <section className="bg-white border border-slate-200" data-testid="section-collection-widget">
      <header className="px-4 py-3 border-b border-slate-200 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-rose-100 flex items-center justify-center">
            <Wallet size={16} className="text-rose-700" />
          </div>
          <div>
            <h3 className="font-display tracking-tight text-[#0B1D3A]">Collection · Keterlambatan & Janji Bayar</h3>
            <p className="text-[10px] text-slate-500">Snapshot AR{asOf ? ` per ${asOf}` : ""} — sumber: engine Rekonsiliasi A.R. · pembukuan resmi di software akuntansi terpisah</p>
          </div>
        </div>
        <button
          data-testid="btn-open-collection"
          onClick={() => nav("/collection")}
          className="flex items-center gap-1 text-xs text-[#FF5A1F] hover:text-[#e04d15] uppercase tracking-wider font-semibold"
        >
          Buka <ChevronRight size={12} />
        </button>
      </header>

      {/* 2026-10-22 (user request "SINKRONISASI ANTREAN"): banner "perlu
          klasifikasi" dibaca dari antrean kanonis /ar/receipts/queue; klik
          langsung membuka tab Antrean Penerimaan di Rekonsiliasi A.R. */}
      {overSummary?.unclassified > 0 && (
        <button type="button" data-testid="collection-widget-needs-classify"
          onClick={() => nav("/finance-report/rekonsiliasi-ar?tab=receipts&filter=unclassified")}
          className="w-full px-4 py-2 bg-amber-50 border-b border-amber-200 flex items-center justify-between gap-2 text-left hover:bg-amber-100 transition-colors">
          <span className="text-[10px] uppercase tracking-wider font-semibold text-amber-800">
            {overSummary.unclassified} proyek perlu klasifikasi penerimaan
            {overSummary.total_items ? ` · ${overSummary.total_items} item` : ""}
            {overSummary.total_excess ? ` · total ${formatIDR(overSummary.total_excess)}` : ""}
            <span className="normal-case tracking-normal font-normal text-amber-700"> — belum tentu invoice resmi belum dibayar; penerimaan belum dikonfirmasi FAT</span>
          </span>
          <ChevronRight size={12} className="text-amber-700 shrink-0" />
        </button>
      )}

      {loading ? (
        <div className="px-4 py-6 text-center text-slate-400 text-xs">Memuat…</div>
      ) : (
        <>
          {/* 3 summary metric */}
          <div className="grid grid-cols-3 border-b border-slate-200">
            <MetricCell
              icon={<ClockAlert size={12} className="text-rose-700" />}
              label="Piutang Jatuh Tempo"
              value={formatIDR(overdueVal)}
              sub={`${overdueCnt} invoice lewat jatuh tempo`}
              tone="rose"
              testid="metric-overdue"
              hint="Σ sisa per-invoice (Kas Harus Diterima − Teralokasi) yang due_date < tanggal acuan — engine Rekonsiliasi A.R. Klik untuk daftar."
              onClick={() => nav("/collection?status=overdue")}
            />
            <MetricCell
              icon={<CalendarCheck size={12} className="text-emerald-700" />}
              label="Janji Bayar 7 Hari"
              value={formatIDR(promiseTotal)}
              sub={`${promiseThisWeek.length} janji`}
              tone="emerald"
              testid="metric-promise"
              hint="Σ janji bayar dengan tanggal 7 hari ke depan — scope komitmen client, bukan piutang."
            />
            <MetricCell
              icon={<TrendingDown size={12} className="text-amber-700" />}
              label="Retensi Belum Diklaim"
              value={formatIDR(retentionVal)}
              sub="belum diklaim (lihat status jatuh tempo di Rekonsiliasi A.R.)"
              tone="amber"
              testid="metric-retention"
              hint="Σ (retensi ditahan − dirilis) untuk retensi inside — engine Rekonsiliasi A.R. Klik untuk daftar."
              onClick={() => nav("/collection?retention=true")}
            />
          </div>

          {/* 2026-10-17 (user request): info snapshot — tanggal & waktu acuan,
              jumlah invoice overdue, total sisa piutang, sumber perhitungan. */}
          {overdueDetail && (
            <div className="px-4 py-1.5 bg-slate-50 border-b border-slate-100 text-[10px] text-slate-500" data-testid="overdue-snapshot-info">
              Snapshot per <b>{fmtDate(overdueDetail.as_of)}</b> · dihitung {new Date(overdueDetail.generated_at).toLocaleString("id-ID")} · <b>{overdueDetail.overdue_count} invoice</b> overdue · total <b>{formatIDR(overdueDetail.overdue_total)}</b> · Sumber: {overdueDetail.source} · {overdueDetail.scope}
            </div>
          )}
          {noDue?.count > 0 && (
            <div className="px-4 py-2 bg-amber-50/60 border-b border-amber-100 text-[10px] text-amber-800" data-testid="overdue-no-due-group">
              Jatuh Tempo Belum Diisi:{" "}
              <button type="button" className="underline hover:text-amber-900 font-semibold"
                data-testid="overdue-no-due-open" onClick={() => nav("/collection?status=no_due_date")}>
                {noDue.count} invoice
              </button>{" "}· total <b>{formatIDR(noDue.total)}</b> — tidak dihitung overdue sampai tanggal jatuh tempo diisi.
            </div>
          )}
          {/* 2026-10-18 (user request): draft/Perlu Verifikasi = kategori
              tersendiri — TIDAK masuk piutang resmi, aging, maupun Top 5. */}
          {/* Statistik kanonis invoice draft (GET /collection/draft-queue/stats) —
              sumber SAMA dengan antrean FAT. Draft TIDAK masuk piutang resmi. */}
          {draftStats?.active_draft_count > 0 && (
            <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 text-[10px] text-slate-600" data-testid="overdue-pending-verif-group">
              <div className="mb-1">Invoice draft (belum diverifikasi final — <b>bukan piutang resmi</b>):</div>
              <div className="flex flex-wrap gap-x-3 gap-y-1">
                {[
                  ["needs_fat", `Perlu Verifikasi FAT: ${draftStats.needs_fat_verification_count} invoice`],
                  ["with_outstanding", `Draft bersisa: ${draftStats.with_outstanding_count} invoice`],
                ].map(([card, label]) => (
                  <button key={card} type="button" className="underline hover:text-slate-900 font-semibold" data-testid={`dash-draft-${card}`}
                    onClick={() => nav(`/collection?tab=incomplete&sub=draft&card=${card}`)}>{label}</button>
                ))}
              </div>
              <div className="mt-1" data-testid="dash-draft-amounts">
                Bruto Draft <b>{formatIDR(draftStats.draft_gross_total)}</b> · Kas Seharusnya Diterima <b>{formatIDR(draftStats.draft_cash_due_total)}</b> · Sisa Draft <b>{formatIDR(draftStats.draft_outstanding_total)}</b>
              </div>
            </div>
          )}

          {/* Top 5 overdue */}
          <div>
            <div className="px-4 py-2 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-wider text-slate-600 font-semibold">Top 5 Terlambat</div>
              {top5.length === 0 && <span className="text-[10px] text-emerald-700 font-semibold uppercase">Tidak Ada Keterlambatan ✓</span>}
            </div>
            {top5.length === 0 ? (
              <div className="px-4 py-4 text-center text-xs text-slate-500 italic" data-testid="top5-empty-state">Tidak ada invoice yang melewati jatuh tempo.</div>
            ) : (
              <ul className="divide-y divide-slate-100" data-testid="top5-overdue-list">
                {top5.map((r) => (
                  // 2026-10-17 (user request): klik item membuka RINCIAN INVOICE
                  // asal (deep-link /collection?focus=<entry_key>), bukan halaman umum.
                  <li key={r.entry_key} data-testid={`overdue-row-${r.entry_key}`}
                    onClick={() => nav(`/collection?focus=${encodeURIComponent(r.entry_key)}`)}
                    className="px-4 py-2 hover:bg-slate-50 cursor-pointer">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold text-slate-800 truncate">{r.client}</div>
                        <div className="text-[10px] text-slate-500 truncate">{r.project_name}{r.invoice_number ? ` · ${r.invoice_number}` : ""}</div>
                        <div className="text-[10px] text-slate-400 mt-0.5">Jatuh tempo {r.due_date ? fmtDate(r.due_date) : "—"} · klik untuk rincian invoice</div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-sm font-bold tabular-nums text-rose-700">{formatIDR(r.remaining)}</div>
                        <div className="text-[10px] text-rose-600 tabular-nums">+{r.days_overdue} hari</div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Janji bayar minggu ini list */}
          {promiseThisWeek.length > 0 && (
            <div>
              <div className="px-4 py-2 bg-emerald-50 border-y border-emerald-100">
                <div className="text-[10px] uppercase tracking-wider text-emerald-800 font-semibold">Janji Bayar Minggu Ini</div>
              </div>
              <ul className="divide-y divide-slate-100" data-testid="promise-list">
                {promiseThisWeek.slice(0, 5).map((p) => (
                  <li key={`${p.client}-${p.project_name}-${p.promise_date}`} className="px-4 py-2 flex items-center justify-between text-xs">
                    <div className="min-w-0 flex-1">
                      <div className="text-slate-800 truncate">{p.client}</div>
                      <div className="text-[10px] text-slate-500 truncate">{p.project_name}</div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-bold tabular-nums text-emerald-700">{p.promise_amount ? formatIDR(p.promise_amount) : "—"}</div>
                      <div className="text-[10px] text-slate-500 tabular-nums">{fmtDate(p.promise_date)}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </section>
  );
}

function MetricCell({ icon, label, value, sub, tone, testid, hint, onClick }) {
  const tones = {
    rose: "border-r-rose-200",
    emerald: "border-r-emerald-200",
    amber: "border-r-amber-200",
  };
  const interactive = typeof onClick === "function";
  return (
    <div
      className={`px-4 py-3 border-r ${tones[tone] || "border-r-slate-100"} last:border-r-0 ${interactive ? "cursor-pointer hover:bg-slate-50 transition-colors" : ""}`}
      data-testid={testid}
      title={hint}
      onClick={onClick}
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={interactive ? (e) => (e.key === "Enter" || e.key === " ") && onClick() : undefined}
    >
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
        {icon} {label}
      </div>
      <div className="font-display text-lg text-[#0B1D3A] tabular-nums leading-tight mt-1">{value}</div>
      <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>
    </div>
  );
}
